import React from 'react';
import { CalendarDays, Download } from 'lucide-react';
import { SiGoogle } from 'react-icons/si';
import type { IconBaseProps } from 'react-icons';
import { formatDate, stripHTML } from 'utils/helpers';
import { EXTERNAL_URLS } from 'utils/constants';
import { getPractice } from 'library/auth';
import moment from 'moment';
import { Dropdown } from 'react-daisyui';

// react-icons returns ReactNode, which React 19's JSX types reject.
// Re-cast to a plain FC so we can use it as a JSX element.
const GoogleIcon = SiGoogle as unknown as React.FC<IconBaseProps>;

// ─── helpers ──────────────────────────────────────────────────────────────────
const eventTitle = (appt, practiceName: string) => {
  const base = practiceName ? `Appointment at ${practiceName}` : 'Appointment';
  return appt.provider.name ? `${base} with ${appt.provider.name}` : base;
};

// "10:00 AM" -> "100000" (HHMMSS, 24h)
const timeToHHMMSS = (t: string): string => {
  const parsed = moment(t, 'h:mm A');
  return parsed.isValid() ? parsed.format('HHmmss') : '000000';
};

const buildEventTimes = (appt) => {
  const dateStr = appt.date.replace(/-/g, '');
  const start = timeToHHMMSS(appt.startTime);
  const end = timeToHHMMSS(appt.endTime);
  return {
    googleStart: `${dateStr}T${start}`,
    googleEnd: `${dateStr}T${end}`
  };
};

const buildGoogleUrl = (appt, practiceName: string, location?: string): string => {
  const { googleStart, googleEnd } = buildEventTimes(appt);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventTitle(appt, practiceName),
    dates: `${googleStart}/${googleEnd}`,
    location: location ?? '',
  });
  return `${EXTERNAL_URLS.GOOGLE_CALENDAR_RENDER}?${params.toString()}`;
};

// DTSTAMP must be the *moment the file was generated* in UTC.
// Format: YYYYMMDDTHHMMSSZ — derived from ISOString stripped of separators.
const nowAsDtstamp = (): string =>
  formatDate(new Date(), 'YYYYMMDDTHH:mm:ssZ')

const buildICSContent = (appt, practiceName: string, location?: string): string => {
  const { googleStart, googleEnd } = buildEventTimes(appt);
  const escape = (s: string) => s.replace(/[\\,;]/g, '\\$&').replace(/\n/g, '\\n');
  // Patient's local timezone — best available since portal data doesn't carry the practice's tz.
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//TeamCare//Patient Portal//EN',
    'BEGIN:VEVENT',
    `UID:appointment-${appt.id}@teamcare`,
    `DTSTAMP:${nowAsDtstamp()}`,
    `DTSTART;TZID=${tzid}:${googleStart}`,
    `DTEND;TZID=${tzid}:${googleEnd}`,
    `SUMMARY:${escape(eventTitle(appt, practiceName))}`,
    `LOCATION:${escape(location ?? '')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

const downloadICS = (appt, practiceName: string, location?: string): void => {
  const blob = new Blob([buildICSContent(appt, practiceName, location)], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `appointment-${appt.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── component ────────────────────────────────────────────────────────────────
interface AddToCalendarProps {
  appointment: any;
  /** Visual preset for the trigger. "default" suits white surfaces; "ghost" suits dark backgrounds. */
  variant?: 'default' | 'ghost';
  /**
   * Classes for the trigger button.
   *  - By default these are appended to the variant preset.
   *  - Pass `replaceTrigger` to drop the preset entirely and fully restyle.
   */
  triggerClassName?: string;
  /** When true, `triggerClassName` replaces the variant preset instead of extending it. */
  replaceTrigger?: boolean;
  /** Custom label, defaults to "Add to Calendar". */
  label?: string;
  /** Override the trigger icon size (px). */
  iconSize?: number;
  /** Hide the trigger icon entirely. */
  hideIcon?: boolean;

  wrapperClassName?: string;
  /** Classes appended to the dropdown panel. */
  menuClassName?: string;
  /** Classes appended to every menu item (link + buttons). */
  itemClassName?: string;
}

const TRIGGER_PRESETS = {
  default:
    'inline-flex items-center justify-center gap-2 text-sm font-medium text-secondary-700 bg-white border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors',
  ghost:
    'inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-md px-2.5 py-1 transition-colors',
};

/**
 * Reusable "Add to Calendar" dropdown.
 */
export const AddToCalendar = ({
  appointment,
  variant = 'default',
  triggerClassName = '',
  replaceTrigger = false,
  label = 'Add to Calendar',
  iconSize,
  hideIcon = false,
  wrapperClassName = '',
  menuClassName = '',
  itemClassName = '',
}: AddToCalendarProps) => {
  let practiceInfo = getPractice()
  const practiceName = practiceInfo?.title ?? '';
  const location = practiceInfo?.address ? stripHTML(practiceInfo.address) : undefined;

  const triggerClasses = replaceTrigger
    ? triggerClassName
    : `${TRIGGER_PRESETS[variant]} ${triggerClassName}`.trim();
  const resolvedIconSize = iconSize ?? (variant === 'ghost' ? 12 : 15);
  const itemBase = 'flex items-center gap-3 px-3 py-2.5 text-sm text-secondary-700 hover:!bg-gray-50 focus:!bg-gray-50 focus:!text-secondary-700 active:!bg-gray-50 active:!text-secondary-700';
  const itemClasses = `${itemBase} ${itemClassName}`.trim();

  return (
    <Dropdown end className={`inline-block ${wrapperClassName}`.trim()}>
      <Dropdown.Toggle
        button={false}
        aria-haspopup="menu"
        role="button"
        className={triggerClasses}
      >
        {!hideIcon && <CalendarDays size={resolvedIconSize} />}
        {label}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className={`w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden animate-scale-in origin-top-right p-0 ${menuClassName}`.trim()}
      >
        <Dropdown.Item
          href={buildGoogleUrl(appointment, practiceName, location)}
          target="_blank"
          rel="noopener noreferrer"
          className={itemClasses}
        >
          <GoogleIcon size={15} color="#4285F4" />
          Google Calendar
        </Dropdown.Item>
        <Dropdown.Item anchor={false}>
          <button
            type="button"
            onClick={() => {
              downloadICS(appointment, practiceName, location);
            }}
            className={`w-full ${itemClasses} border-t border-gray-100`}
          >
            <Download size={15} className="text-secondary-500" />
            Download .ics file
          </button>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};
